import * as React from 'react';
import SelectionExt from '../../common/SelectionExt';
import { Quantization } from '../../../entities/common';
import { IAssignmentByDayData, ITaskBaselineByDay } from '../../../entities/Subentities';
import ViewMode from './ViewMode';
import { SummaryData, TimeType } from './common';
import { toDate } from '../../utils/common';
import EditMode from './EditMode';
import { css } from 'office-ui-fabric-react';

type OwnProps = {
    byDays: IAssignmentByDayData[];
    taskBaselineByDays?: ITaskBaselineByDay[];
    selection?: SelectionExt;
    selectionIndex?: number;
    onClick?: React.MouseEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    onKeyPress?: React.KeyboardEventHandler;
    onFocus?: React.FocusEventHandler;
    quantization: Quantization;
    startDate: Date;
    finishDate: Date;
    onEditComplete: ((effort: number | null, completedWork: number | null, remainingWork: number | null) => void) | undefined;
    onEditStart?: () => void;
    componentRef?: React.LegacyRef<HTMLDivElement>;
    timeType: TimeType[];
    isNonWorking: boolean;
}

const WorkloadGridCell = (props: OwnProps) => {
    const { onClick, onKeyDown, onKeyPress, onFocus, componentRef, selectionIndex, timeType, quantization, isNonWorking } = props;
    const [editTimeType, setEditTimeType] = React.useState<TimeType | undefined>();
    const [summaryData, setSummaryData] = React.useState<SummaryData>({ effort: 0, completedWork: 0, remainingWork: 0, hasValue: false });
    const [baselineEffort, setBaselineEffort] = React.useState<number>();
    const [isSelected, setIsSelected] = React.useState<boolean>();
    
    React.useEffect(() => setSummaryData(calculateSummaryData(props.byDays, props.startDate, props.finishDate)), [props.byDays, props.startDate, props.finishDate]);
    React.useEffect(() => setBaselineEffort(calculateBaselineEffort(props.taskBaselineByDays, props.startDate, props.finishDate)), [props.taskBaselineByDays, props.startDate, props.finishDate]);
    React.useEffect(() => {
        if (props.selectionIndex !== undefined) {
            props.selection?.AddEventHandler(onSelectionChanged, props.selectionIndex);
        }
        return () => {
            if (props.selectionIndex !== undefined) {
                props.selection?.RemoveEventHandler(onSelectionChanged);
            }
        }
    }, [props.selectionIndex]);

    const onSelectionChanged = () => {
        const newIsSelected = props.selection!.isIndexSelected(props.selectionIndex!);
        if (isSelected !== newIsSelected) {
            setIsSelected(newIsSelected);
        }
    }

    const isEditable = quantization === Quantization.days && selectionIndex !== undefined;
    const editableTimeType: TimeType[] = [];
    if(isEditable) {
        editableTimeType.push('CompletedWork');
        if(!isNonWorking){
            editableTimeType.push('Effort');
        }
    }
    const isEditMode = editTimeType !== undefined;

    const startEditing = (_editTimeType?: TimeType) => {
        if (_editTimeType === undefined) {
            const { timeType } = props;
            if (timeType.includes('Effort') && editableTimeType.includes('Effort')) {
                _editTimeType = 'Effort';
            } else if (timeType.includes('CompletedWork') && editableTimeType.includes('CompletedWork')) {
                _editTimeType = 'CompletedWork';
            }
        }

        if (_editTimeType === undefined || !props.onEditStart) {
            return;
        }

        props.onEditStart?.();
        setEditTimeType(_editTimeType);
    }

    return <div
        tabIndex={isEditable ? 0 : undefined}
        ref={componentRef}
        data-selection-index={selectionIndex}
        onClick={onClick}
        onFocus={onFocus}
        onDoubleClick={isEditable ? () => startEditing() : undefined}
        onKeyDown={onKeyDown}
        onKeyPress={isEditable ? e => {
            if (!isEditMode && (e.key === 'Enter' || e.charCode >= 48 && e.charCode <= 57)) {
                startEditing();
            }
            onKeyPress?.(e)
        } : undefined}
        className={css('resource-usage-cell fill-space', isSelected && 'selected', isEditMode && 'edit-mode')}>
        <div className={`cell-content fill-space columns-${timeType.length}`}>
            {isEditMode
                ? <EditMode
                    timeType={editTimeType!}
                    summaryData={summaryData}
                    onEditComplete={(effort, completedWork, remainingWork) => {
                        if ((summaryData.effort ?? 0) !== (effort ?? 0) || summaryData.completedWork !== completedWork || (summaryData.remainingWork ?? 0) !== (remainingWork ?? 0)) {
                            setSummaryData({ ...summaryData, effort, completedWork, remainingWork })
                            props.onEditComplete?.(effort, completedWork, remainingWork);
                        }
                        setEditTimeType(undefined);
                    }} />
                : <ViewMode
                    startEditing={isEditable ? startEditing : undefined}
                    summaryData={summaryData}
                    baselineEffort={baselineEffort}
                    timeType={timeType}
                    isNonWorking={isNonWorking}
                    editableTimeType={editableTimeType} />}
        </div>
    </div>;
}

export default WorkloadGridCell;

function calculateSummaryData(byDays: IAssignmentByDayData[], startDate: Date, finishDate: Date): SummaryData {
    return byDays.reduce<SummaryData>((acc, curr) => {
        const currDate = toDate(curr.Date)!;
        if (currDate >= startDate && currDate <= finishDate) {
            if(curr.Effort != null){
                acc.effort ??= 0;
                acc.effort += curr.Effort;
            }
            if(curr.RemainingWork != null){
                acc.remainingWork ??= 0;
                acc.remainingWork += curr.RemainingWork;
            }
            acc.completedWork += curr.CompletedWork;
            acc.hasValue ||= true;
        }
        return acc;
    }, { effort: null, completedWork: 0, remainingWork: null, hasValue: false });
}

function calculateBaselineEffort(byDays: ITaskBaselineByDay[] | undefined, startDate: Date, finishDate: Date): number | undefined {
    if(!byDays){
        return undefined;
    }
    return byDays.reduce<number>((acc, curr) => {
        const currDate = toDate(curr.attributes.Date)!;
        if (currDate >= startDate && currDate <= finishDate) {
            acc += curr.attributes.Effort;
        }
        return acc;
    }, 0);
}
